import * as React from "react"
import { Head, router, useForm, usePage } from "@inertiajs/react"
import {
  Edit2,
  GitMerge,
  Loader2,
  Plus,
  Search,
  Trash2,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import {
  DataCard,
  DataCardField,
  DataCardGrid,
  DataCardHeader,
  DataCardList,
  DataCardTitle,
} from "@/components/ui/data-card"
import type { PageProps } from "@/types/inertia"
import { cn } from "@/lib/utils"
import { consumer } from "@/lib/actioncable"

type ProductMappingRow = {
  id: number
  product_code: string
  product_name: string
  distributor_sap_code: string | null
  product_distributor_code: string | null
  product_distributor_name: string | null
  source: string
  created_at: string
  updated_at: string
}

type ProductParentRow = {
  id: number
  name: string
  code: string
  associated_mappings_count: number
  product_mappings: ProductMappingRow[]
  created_at: string
  updated_at: string
}

type Pagination = {
  total_count: number
  page: number
  per_page: number
}

type Filters = {
  search: string | null
  distributor_sap_code: string | null
  source: string | null
  parents_page: number
  unmapped_page: number
}

type Props = {
  parents: ProductParentRow[]
  unmapped_mappings: ProductMappingRow[]
  all_parents: { id: number; name: string }[]
  available_distributor_sap_codes: string[]
  available_sources: string[]
  parents_pagination: Pagination
  unmapped_pagination: Pagination
  filters: Filters
  matching_active: boolean
}

export default function AdminProductParentChildIndex() {
  const { props } = usePage<PageProps<Props>>()
  const {
    parents,
    unmapped_mappings,
    all_parents,
    available_distributor_sap_codes,
    available_sources,
    parents_pagination,
    unmapped_pagination,
    filters,
    matching_active,
  } = props

  const [activeTab, setActiveTab] = React.useState<"parents" | "unmapped">("parents")
  const [selectedParentIds, setSelectedParentIds] = React.useState<Set<number>>(new Set())
  const [selectedMappingIds, setSelectedMappingIds] = React.useState<Set<number>>(new Set())
  const [searchValue, setSearchValue] = React.useState(filters.search ?? "")

  const [matchingStatus, setMatchingStatus] = React.useState<string | null>(
    matching_active ? "started" : null
  )
  const [matchingMessage, setMatchingMessage] = React.useState<string>(
    matching_active ? "Pencocokan sedang berlangsung..." : ""
  )

  React.useEffect(() => {
    const sub = consumer.subscriptions.create(
      { channel: "Admin::ProductMatchChannel" },
      {
        received(data: { status: string; message: string }) {
          if (data.status === "completed") {
            setMatchingStatus(null)
            setMatchingMessage("")
            router.reload()
          } else {
            setMatchingStatus(data.status)
            setMatchingMessage(data.message)
          }
        },
      }
    )

    return () => {
      sub.unsubscribe()
    }
  }, [])

  // Modal triggers
  const [createOpen, setCreateOpen] = React.useState(false)
  const [editParent, setEditParent] = React.useState<ProductParentRow | null>(null)
  const [deleteParent, setDeleteParent] = React.useState<ProductParentRow | null>(null)
  const [mergeOpen, setMergeOpen] = React.useState(false)
  const [drawerParent, setDrawerParent] = React.useState<ProductParentRow | null>(null)

  // Remapping state
  const [mappingsToRemap, setMappingsToRemap] = React.useState<ProductMappingRow[]>([])
  const [remapTargetParentId, setRemapTargetParentId] = React.useState<string>("")
  const [remapSearch, setRemapSearch] = React.useState("")

  // Merge state
  const [mergeTargetId, setMergeTargetId] = React.useState<string>("")
  const [mergeSearch, setMergeSearch] = React.useState("")

  // Sync drawer parent to get live updates when mapping list updates
  React.useEffect(() => {
    if (drawerParent) {
      const updated = parents.find((p) => p.id === drawerParent.id)
      setDrawerParent(updated || null)
    }
  }, [parents])

  // Reset selections when tab changes
  React.useEffect(() => {
    setSelectedParentIds(new Set())
    setSelectedMappingIds(new Set())
  }, [activeTab])

  React.useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  const totalParentsPages = Math.ceil(parents_pagination.total_count / parents_pagination.per_page)
  const totalUnmappedPages = Math.ceil(unmapped_pagination.total_count / unmapped_pagination.per_page)

  function navigate(overrides: Record<string, string | number | null>) {
    const params: Record<string, string | number> = {}
    if (filters.search) params.search = filters.search
    if (filters.distributor_sap_code) params.distributor_sap_code = filters.distributor_sap_code
    if (filters.source) params.source = filters.source
    if (filters.parents_page > 1) params.parents_page = filters.parents_page
    if (filters.unmapped_page > 1) params.unmapped_page = filters.unmapped_page

    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") {
        params[k] = v
      } else {
        delete params[k]
      }
    })

    router.get("/admin/product/parent-child", params as Record<string, string>, {
      preserveScroll: true,
    })
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    navigate({ search: searchValue || null, parents_page: 1, unmapped_page: 1 })
  }

  function handleDistributorChange(e: React.ChangeEvent<HTMLSelectElement>) {
    navigate({ distributor_sap_code: e.target.value || null, parents_page: 1, unmapped_page: 1 })
  }

  function handleSourceChange(e: React.ChangeEvent<HTMLSelectElement>) {
    navigate({ source: e.target.value || null, parents_page: 1, unmapped_page: 1 })
  }

  function handleTriggerAutoMatch() {
    router.post("/admin/product/parent-child/auto_match", {}, {
      preserveScroll: true,
    })
  }

  // Parents catalog selection
  function toggleSelectParent(id: number) {
    setSelectedParentIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAllParents() {
    if (selectedParentIds.size === parents.length && parents.length > 0) {
      setSelectedParentIds(new Set())
    } else {
      setSelectedParentIds(new Set(parents.map((p) => p.id)))
    }
  }

  // Unmapped mappings selection
  function toggleSelectMapping(id: number) {
    setSelectedMappingIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAllMappings() {
    if (selectedMappingIds.size === unmapped_mappings.length && unmapped_mappings.length > 0) {
      setSelectedMappingIds(new Set())
    } else {
      setSelectedMappingIds(new Set(unmapped_mappings.map((m) => m.id)))
    }
  }

  // Create Form
  const createForm = useForm({
    name: "",
    code: "",
  })

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    createForm.post("/admin/product/parent-child", {
      onSuccess: () => {
        createForm.reset()
        setCreateOpen(false)
      },
    })
  }

  // Edit Form
  const editForm = useForm({
    name: "",
    code: "",
  })

  React.useEffect(() => {
    if (editParent) {
      editForm.setData({
        name: editParent.name,
        code: editParent.code,
      })
    }
  }, [editParent])

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editParent) return
    editForm.patch(`/admin/product/parent-child/${editParent.id}`, {
      onSuccess: () => {
        editForm.reset()
        setEditParent(null)
      },
    })
  }

  // Delete Confirm
  function handleDeleteConfirm() {
    if (!deleteParent) return
    router.delete(`/admin/product/parent-child/${deleteParent.id}`, {
      onFinish: () => setDeleteParent(null),
    })
  }

  // Merge Submit
  function handleMergeSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!mergeTargetId) return
    router.post(
      "/admin/product/parent-child/merge",
      {
        source_parent_ids: Array.from(selectedParentIds),
        target_parent_id: Number(mergeTargetId),
      },
      {
        onSuccess: () => {
          setSelectedParentIds(new Set())
          setMergeOpen(false)
          setMergeTargetId("")
          setMergeSearch("")
        },
      }
    )
  }

  // Remap Submit
  function handleRemapSubmit(e: React.FormEvent) {
    e.preventDefault()
    router.patch(
      "/admin/product/parent-child/bulk_update_mappings",
      {
        product_mapping_ids: mappingsToRemap.map((m) => m.id),
        product_parent_id:
          remapTargetParentId === "null" || remapTargetParentId === ""
            ? "null"
            : Number(remapTargetParentId),
      },
      {
        onSuccess: () => {
          setMappingsToRemap([])
          setSelectedMappingIds(new Set())
          setRemapTargetParentId("")
          setRemapSearch("")
        },
      }
    )
  }

  // Row-level disassociate action inside parent detail drawer
  function handleDisassociateMapping(mappingId: number) {
    if (!window.confirm("Apakah Anda yakin ingin melepas pemetaan produk ini?")) {
      return
    }
    router.patch(
      "/admin/product/parent-child/bulk_update_mappings",
      {
        product_mapping_ids: [mappingId],
        product_parent_id: "null",
      },
      {
        preserveScroll: true,
      }
    )
  }

  const notice = props.flash?.notice
  const alert = props.flash?.alert

  // Filtered dropdown lists based on local search inputs
  const filteredMergeParents = all_parents.filter(
    (p) =>
      !selectedParentIds.has(p.id) &&
      p.name.toLowerCase().includes(mergeSearch.toLowerCase())
  )

  const filteredRemapParents = all_parents.filter((p) =>
    p.name.toLowerCase().includes(remapSearch.toLowerCase())
  )

  return (
    <>
      <Head title="Product Parent-Child">
        <meta
          name="description"
          content="Consolidate and group variant distributor products under single virtual parent products."
        />
        <meta property="og:title" content="Product Parent-Child" />
        <meta
          property="og:description"
          content="Consolidate and group variant distributor products under single virtual parent products."
        />
      </Head>

      <AppShell>
        <PageHeader
          title="Product Parent-Child"
          description="Kelola produk induk virtual untuk menyatukan variasi nama/kode produk distributor."
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleTriggerAutoMatch}
                disabled={matchingStatus !== null}
              >
                Trigger Auto-Match
              </Button>
              <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1.5 h-4 w-4" />
                Tambah Produk Induk
              </Button>
            </div>
          }
        />

        {/* Live Matching Progress Banner */}
        {matchingStatus !== null && (
          <div className="mt-4 flex items-center gap-3 rounded-lg border border-accent/20 bg-accent/5 p-4 text-accent-display">
            <Loader2 className="h-5 w-5 animate-spin text-accent shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Proses Auto-Match Sedang Berjalan</p>
              <p className="text-xs text-ink-muted mt-0.5 truncate">{matchingMessage}</p>
            </div>
          </div>
        )}

        {/* Status Notices */}
        {notice && <p className="mt-4 text-sm text-accent">{notice}</p>}
        {alert && <p className="mt-4 text-sm text-danger-display">{alert}</p>}

        {/* Global Filters and Search */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={filters.distributor_sap_code ?? ""}
              onChange={handleDistributorChange}
              className="w-48"
            >
              <option value="">Semua Distributor</option>
              {available_distributor_sap_codes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>

            <Select
              value={filters.source ?? ""}
              onChange={handleSourceChange}
              className="w-48"
            >
              <option value="">Semua Source Table</option>
              {available_sources.map((src) => (
                <option key={src} value={src}>
                  {src}
                </option>
              ))}
            </Select>

            {(filters.search || filters.distributor_sap_code || filters.source) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  navigate({ search: null, distributor_sap_code: null, source: null })
                }
              >
                Reset filter
              </Button>
            )}
          </div>

          <form onSubmit={handleSearchSubmit} className="relative w-full max-w-xs sm:w-auto">
            <Input
              type="search"
              placeholder="Cari kode atau nama..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={() => navigate({ search: searchValue || null, parents_page: 1, unmapped_page: 1 })}
              className="pl-9 pr-4"
            />
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted pointer-events-none" />
          </form>
        </div>

        {/* Navigation Tabs */}
        <nav className="mt-6 flex items-end justify-between gap-4 border-b border-hairline">
          <div className="flex items-center gap-6">
            <button
              onClick={() => setActiveTab("parents")}
              className={cn(
                "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm font-medium no-underline transition-colors",
                activeTab === "parents"
                  ? "border-accent text-accent-display"
                  : "border-transparent text-ink-body hover:text-ink-display"
              )}
            >
              Parents Catalog
            </button>
            <button
              onClick={() => setActiveTab("unmapped")}
              className={cn(
                "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm font-medium no-underline transition-colors",
                activeTab === "unmapped"
                  ? "border-accent text-accent-display"
                  : "border-transparent text-ink-body hover:text-ink-display"
              )}
            >
              Unmapped Products
            </button>
          </div>
        </nav>

        {/* --- PARENTS TAB --- */}
        {activeTab === "parents" && (
          <div className="mt-4">
            {/* Merge parents bulk action bar */}
            {selectedParentIds.size > 1 && (
              <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-hairline bg-surface px-4 py-2">
                <span className="text-sm font-medium text-ink-display">
                  {selectedParentIds.size} produk induk terpilih
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="primary" size="sm" onClick={() => setMergeOpen(true)}>
                    <GitMerge className="mr-1.5 h-3.5 w-3.5" />
                    Gabungkan Produk Induk
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedParentIds(new Set())}
                  >
                    Batal
                  </Button>
                </div>
              </div>
            )}

            {parents.length === 0 ? (
              <div className="rounded-md border border-hairline bg-page p-8 text-center text-ink-muted">
                Tidak ada produk induk ditemukan.
              </div>
            ) : (
              <>
                {/* Desktop Parents Table */}
                <div className="hidden overflow-hidden rounded-md border border-hairline md:block bg-page">
                  <table className="w-full text-sm">
                    <thead className="bg-surface">
                      <tr>
                        <th className="w-10 px-3 py-2.5">
                          <Checkbox
                            checked={parents.length > 0 && selectedParentIds.size === parents.length}
                            onChange={toggleSelectAllParents}
                            aria-label="Pilih semua"
                          />
                        </th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Kode Parent</th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Nama Parent</th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted text-center">Jumlah Child</th>
                        <th className="w-24 px-3 py-2.5 text-right font-medium text-ink-muted">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline">
                      {parents.map((parent) => (
                        <tr
                          key={parent.id}
                          className="hover:bg-surface/20 cursor-pointer"
                          onClick={() => setDrawerParent(parent)}
                        >
                          <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedParentIds.has(parent.id)}
                              onChange={() => toggleSelectParent(parent.id)}
                              aria-label={`Pilih ${parent.name}`}
                            />
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-ink-display">{parent.code}</td>
                          <td className="px-4 py-2.5 font-medium text-ink-display">{parent.name}</td>
                          <td className="px-4 py-2.5 text-ink-body text-center">
                            <Badge tone={parent.associated_mappings_count > 0 ? "accent" : "neutral"}>
                              {parent.associated_mappings_count}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditParent(parent)}
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4 text-ink-muted hover:text-ink-body" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteParent(parent)}
                                title="Hapus"
                              >
                                <Trash2 className="h-4 w-4 text-ink-muted hover:text-danger-display" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Parents Cards */}
                <div className="md:hidden">
                  <DataCardList>
                    {parents.map((parent) => (
                      <DataCard key={parent.id} onClick={() => setDrawerParent(parent)}>
                        <DataCardHeader>
                          <div className="flex items-center gap-3">
                            <div onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedParentIds.has(parent.id)}
                                onChange={() => toggleSelectParent(parent.id)}
                                aria-label={`Pilih ${parent.name}`}
                              />
                            </div>
                            <DataCardTitle className="font-semibold">{parent.name}</DataCardTitle>
                          </div>
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" onClick={() => setEditParent(parent)}>
                              <Edit2 className="h-4 w-4 text-ink-muted" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeleteParent(parent)}>
                              <Trash2 className="h-4 w-4 text-ink-muted" />
                            </Button>
                          </div>
                        </DataCardHeader>
                        <DataCardGrid>
                          <DataCardField label="Kode Parent" value={parent.code} />
                          <DataCardField
                            label="Jumlah Mapped Child"
                            value={
                              <Badge tone={parent.associated_mappings_count > 0 ? "accent" : "neutral"}>
                                {parent.associated_mappings_count}
                              </Badge>
                            }
                          />
                        </DataCardGrid>
                      </DataCard>
                    ))}
                  </DataCardList>
                </div>

                {/* Parents Pagination */}
                {totalParentsPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={parents_pagination.page <= 1}
                      onClick={() => navigate({ parents_page: parents_pagination.page - 1 })}
                    >
                      Sebelumnya
                    </Button>
                    <span className="text-sm text-ink-muted">
                      Halaman {parents_pagination.page} dari {totalParentsPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={parents_pagination.page >= totalParentsPages}
                      onClick={() => navigate({ parents_page: parents_pagination.page + 1 })}
                    >
                      Selanjutnya
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* --- UNMAPPED PRODUCTS TAB --- */}
        {activeTab === "unmapped" && (
          <div className="mt-4">
            {/* Remap selected action bar */}
            {selectedMappingIds.size > 0 && (
              <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-hairline bg-surface px-4 py-2">
                <span className="text-sm font-medium text-ink-display">
                  {selectedMappingIds.size} produk terpilih
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      const selected = unmapped_mappings.filter((m) => selectedMappingIds.has(m.id))
                      setMappingsToRemap(selected)
                    }}
                  >
                    Petakan ke Induk
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMappingIds(new Set())}
                  >
                    Batal
                  </Button>
                </div>
              </div>
            )}

            {unmapped_mappings.length === 0 ? (
              <div className="rounded-md border border-hairline bg-page p-8 text-center text-ink-muted">
                Semua produk distributor sudah terpetakan! Tidak ada produk unmapped.
              </div>
            ) : (
              <>
                {/* Desktop Unmapped Table */}
                <div className="hidden overflow-hidden rounded-md border border-hairline md:block bg-page">
                  <table className="w-full text-sm">
                    <thead className="bg-surface">
                      <tr>
                        <th className="w-10 px-3 py-2.5">
                          <Checkbox
                            checked={unmapped_mappings.length > 0 && selectedMappingIds.size === unmapped_mappings.length}
                            onChange={toggleSelectAllMappings}
                            aria-label="Pilih semua"
                          />
                        </th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Kode Produk</th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Nama Produk</th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted">SAP Distributor</th>
                        <th className="px-4 py-2.5 text-left font-medium text-ink-muted">Source</th>
                        <th className="w-24 px-3 py-2.5 text-right font-medium text-ink-muted">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-hairline">
                      {unmapped_mappings.map((mapping) => (
                        <tr key={mapping.id} className="hover:bg-surface/20">
                          <td className="px-3 py-2.5">
                            <Checkbox
                              checked={selectedMappingIds.has(mapping.id)}
                              onChange={() => toggleSelectMapping(mapping.id)}
                              aria-label={`Pilih ${mapping.product_name}`}
                            />
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-ink-display">{mapping.product_code}</td>
                          <td className="px-4 py-2.5 font-medium text-ink-display">
                            <div>{mapping.product_name}</div>
                            {mapping.product_distributor_name && (
                              <div className="text-xs text-ink-muted">
                                Dist: {mapping.product_distributor_name} ({mapping.product_distributor_code})
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-ink-body">{mapping.distributor_sap_code || "—"}</td>
                          <td className="px-4 py-2.5 text-ink-body">
                            <Badge tone="neutral" className="capitalize">
                              {mapping.source.replace("_", " ")}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setMappingsToRemap([mapping])}
                            >
                              Petakan
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Unmapped Cards */}
                <div className="md:hidden">
                  <DataCardList>
                    {unmapped_mappings.map((mapping) => (
                      <DataCard key={mapping.id}>
                        <DataCardHeader>
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selectedMappingIds.has(mapping.id)}
                              onChange={() => toggleSelectMapping(mapping.id)}
                              aria-label={`Pilih ${mapping.product_name}`}
                            />
                            <DataCardTitle className="font-semibold">{mapping.product_name}</DataCardTitle>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setMappingsToRemap([mapping])}
                          >
                            Petakan
                          </Button>
                        </DataCardHeader>
                        <DataCardGrid>
                          <DataCardField label="Kode Produk" value={mapping.product_code} />
                          <DataCardField label="SAP Distributor" value={mapping.distributor_sap_code || "—"} />
                          <DataCardField
                            label="Distributor Nama/Kode"
                            value={
                              mapping.product_distributor_name
                                ? `${mapping.product_distributor_name} (${mapping.product_distributor_code})`
                                : "—"
                            }
                            wide
                          />
                          <DataCardField
                            label="Source"
                            value={
                              <Badge tone="neutral" className="capitalize">
                                {mapping.source.replace("_", " ")}
                              </Badge>
                            }
                          />
                        </DataCardGrid>
                      </DataCard>
                    ))}
                  </DataCardList>
                </div>

                {/* Unmapped Pagination */}
                {totalUnmappedPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={unmapped_pagination.page <= 1}
                      onClick={() => navigate({ unmapped_page: unmapped_pagination.page - 1 })}
                    >
                      Sebelumnya
                    </Button>
                    <span className="text-sm text-ink-muted">
                      Halaman {unmapped_pagination.page} dari {totalUnmappedPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={unmapped_pagination.page >= totalUnmappedPages}
                      onClick={() => navigate({ unmapped_page: unmapped_pagination.page + 1 })}
                    >
                      Selanjutnya
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* --- DIALOGS --- */}

        {/* Dialog: Create Parent */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Tambah Produk Induk Baru</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSubmit} className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="create_name">Nama Produk Induk</label>
                <Input
                  id="create_name"
                  required
                  placeholder="Masukkan nama produk..."
                  value={createForm.data.name}
                  onChange={(e) => createForm.setData("name", e.target.value)}
                />
                {createForm.errors.name && (
                  <p className="text-xs text-danger-display">{createForm.errors.name}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label htmlFor="create_code">Kode Parent (Opsional)</label>
                <Input
                  id="create_code"
                  placeholder="Contoh: PP-XXXXXX (Auto-generate jika kosong)"
                  value={createForm.data.code}
                  onChange={(e) => createForm.setData("code", e.target.value)}
                />
                {createForm.errors.code && (
                  <p className="text-xs text-danger-display">{createForm.errors.code}</p>
                )}
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Batal</Button>
                </DialogClose>
                <Button variant="primary" type="submit" disabled={createForm.processing}>
                  {createForm.processing ? "Menambahkan..." : "Tambah Parent"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Edit Parent */}
        <Dialog open={editParent !== null} onOpenChange={(open) => !open && setEditParent(null)}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Edit Produk Induk</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="edit_name">Nama Produk Induk</label>
                <Input
                  id="edit_name"
                  required
                  value={editForm.data.name}
                  onChange={(e) => editForm.setData("name", e.target.value)}
                />
                {editForm.errors.name && (
                  <p className="text-xs text-danger-display">{editForm.errors.name}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <label htmlFor="edit_code">Kode Parent</label>
                <Input
                  id="edit_code"
                  required
                  value={editForm.data.code}
                  onChange={(e) => editForm.setData("code", e.target.value)}
                />
                {editForm.errors.code && (
                  <p className="text-xs text-danger-display">{editForm.errors.code}</p>
                )}
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Batal</Button>
                </DialogClose>
                <Button variant="primary" type="submit" disabled={editForm.processing}>
                  {editForm.processing ? "Menyimpan..." : "Simpan Perubahan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Delete Confirm */}
        <Dialog open={deleteParent !== null} onOpenChange={(open) => !open && setDeleteParent(null)}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Hapus Produk Induk?</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-ink-body">
                Apakah Anda yakin ingin menghapus produk induk <strong>{deleteParent?.name}</strong> ({deleteParent?.code})?
                <br />
                Semua pemetaan produk child di dalamnya akan dilepaskan (unmapped).
              </p>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Batal</Button>
                </DialogClose>
                <Button variant="danger" type="button" onClick={handleDeleteConfirm}>
                  Hapus Parent
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Merge Parents */}
        <Dialog open={mergeOpen} onOpenChange={(open) => !open && setMergeOpen(open)}>
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>Gabungkan Produk Induk</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleMergeSubmit} className="mt-4 space-y-4">
              <div className="rounded-md border border-hairline bg-surface p-3 space-y-1.5">
                <span className="text-xs text-ink-muted">Produk induk yang akan digabungkan (sumber):</span>
                <ul className="list-disc pl-5 text-sm font-semibold text-ink-display">
                  {parents
                    .filter((p) => selectedParentIds.has(p.id))
                    .map((p) => (
                      <li key={p.id}>
                        {p.name} ({p.code})
                      </li>
                    ))}
                </ul>
              </div>

              <div className="space-y-1.5">
                <label>Pilih target produk induk yang bertahan:</label>
                <div className="space-y-2">
                  <Input
                    placeholder="Cari produk induk target..."
                    value={mergeSearch}
                    onChange={(e) => setMergeSearch(e.target.value)}
                  />
                  <div className="max-h-40 overflow-y-auto border border-hairline rounded-md divide-y divide-hairline bg-page">
                    {filteredMergeParents.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setMergeTargetId(String(p.id))}
                        className={cn(
                          "w-full text-left px-3 py-2 text-sm transition-colors",
                          mergeTargetId === String(p.id)
                            ? "bg-accent-faded text-accent-display font-medium"
                            : "text-ink-body hover:bg-surface"
                        )}
                      >
                        {p.name}
                      </button>
                    ))}
                    {filteredMergeParents.length === 0 && (
                      <div className="p-3 text-sm text-ink-muted text-center">
                        Tidak ada produk induk ditemukan.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button" onClick={() => {
                    setMergeOpen(false)
                    setMergeTargetId("")
                    setMergeSearch("")
                  }}>Batal</Button>
                </DialogClose>
                <Button variant="primary" type="submit" disabled={!mergeTargetId}>
                  Gabungkan
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Remap Child Mappings */}
        <Dialog open={mappingsToRemap.length > 0} onOpenChange={(open) => !open && setMappingsToRemap([])}>
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle>Petakan Produk ke Induk</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRemapSubmit} className="mt-4 space-y-4">
              <div className="rounded-md border border-hairline bg-surface p-3 space-y-1.5 max-h-32 overflow-y-auto">
                <span className="text-xs text-ink-muted">Produk distributor yang akan dipetakan:</span>
                <ul className="list-disc pl-5 text-sm font-semibold text-ink-display">
                  {mappingsToRemap.map((m) => (
                    <li key={m.id}>
                      {m.product_name} ({m.product_code})
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-1.5">
                <label>Pilih target produk induk:</label>
                <div className="space-y-2">
                  <Input
                    placeholder="Cari produk induk..."
                    value={remapSearch}
                    onChange={(e) => setRemapSearch(e.target.value)}
                  />
                  <div className="max-h-48 overflow-y-auto border border-hairline rounded-md divide-y divide-hairline bg-page">
                    <button
                      type="button"
                      onClick={() => setRemapTargetParentId("null")}
                      className={cn(
                        "w-full text-left px-3 py-2 text-sm transition-colors font-medium border-b border-hairline",
                        remapTargetParentId === "null"
                          ? "bg-accent-faded text-accent-display"
                          : "text-ink-body hover:bg-surface"
                      )}
                    >
                      — Lepaskan Pemetaan (Unmap) —
                    </button>
                    {filteredRemapParents.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setRemapTargetParentId(String(p.id))}
                        className={cn(
                          "w-full text-left px-3 py-2 text-sm transition-colors",
                          remapTargetParentId === String(p.id)
                            ? "bg-accent-faded text-accent-display font-medium"
                            : "text-ink-body hover:bg-surface"
                        )}
                      >
                        {p.name}
                      </button>
                    ))}
                    {filteredRemapParents.length === 0 && (
                      <div className="p-3 text-sm text-ink-muted text-center">
                        Tidak ada produk induk ditemukan.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button" onClick={() => {
                    setMappingsToRemap([])
                    setRemapTargetParentId("")
                    setRemapSearch("")
                  }}>Batal</Button>
                </DialogClose>
                <Button variant="primary" type="submit" disabled={!remapTargetParentId}>
                  Terapkan Pemetaan
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog: Detail Children Drawer / Modal */}
        <Dialog open={drawerParent !== null} onOpenChange={(open) => !open && setDrawerParent(null)}>
          <DialogContent size="lg">
            <DialogHeader>
              <DialogTitle>Detail Pemetaan Child — {drawerParent?.name}</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 rounded-md border border-hairline bg-surface p-3 text-sm">
                <div>
                  <span className="text-xs text-ink-muted">Nama Produk Induk</span>
                  <div className="font-semibold text-ink-display">{drawerParent?.name}</div>
                </div>
                <div>
                  <span className="text-xs text-ink-muted">Kode Produk Induk</span>
                  <div className="font-semibold text-ink-display font-mono">{drawerParent?.code}</div>
                </div>
              </div>

              <div>
                <span className="text-sm font-semibold text-ink-display">Child Mappings Terkait ({drawerParent?.product_mappings?.length || 0})</span>
                <div className="mt-2 max-h-80 overflow-y-auto rounded-md border border-hairline bg-page">
                  {(!drawerParent?.product_mappings || drawerParent.product_mappings.length === 0) ? (
                    <div className="p-6 text-center text-sm text-ink-muted">
                      Tidak ada child mapping untuk parent ini.
                    </div>
                  ) : (
                    <table className="w-full text-sm text-left">
                      <thead className="bg-surface text-ink-muted sticky top-0 border-b border-hairline">
                        <tr>
                          <th className="px-3 py-2">Kode</th>
                          <th className="px-3 py-2">Nama</th>
                          <th className="px-3 py-2">Dist SAP</th>
                          <th className="px-3 py-2">Source</th>
                          <th className="px-3 py-2 text-right">Aksi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-hairline">
                        {drawerParent.product_mappings.map((mapping) => (
                          <tr key={mapping.id} className="hover:bg-surface/10">
                            <td className="px-3 py-2 font-mono text-xs text-ink-display">{mapping.product_code}</td>
                            <td className="px-3 py-2">
                              <div className="text-ink-body font-medium">{mapping.product_name}</div>
                              {mapping.product_distributor_name && (
                                <div className="text-[10px] text-ink-muted">
                                  {mapping.product_distributor_name} ({mapping.product_distributor_code})
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-ink-body">{mapping.distributor_sap_code || "—"}</td>
                            <td className="px-3 py-2">
                              <Badge tone="neutral" className="capitalize text-[10px]">
                                {mapping.source.replace("_", " ")}
                              </Badge>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setMappingsToRemap([mapping])}
                                >
                                  Remap
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-danger-display hover:text-danger-display"
                                  onClick={() => handleDisassociateMapping(mapping.id)}
                                >
                                  Unmap
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Tutup</Button>
                </DialogClose>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </AppShell>
    </>
  )
}
